import {
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import * as Sentry from "@sentry/react";
import _route from "./constants/routes";
import PageLayouts from "./pages/layout";
import PrivateRoute from "./routeguard/PrivateRoute";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import Verify from "./pages/auth/Verify";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Team from "./pages/Team";
import Submission from "./pages/Submission";
import ErrorPage from "./pages/ErrorPage";
import AdminSubmissions from "./pages/admin/AdminSubmissions";
import AdminSubmissionDetail from "./pages/admin/AdminSubmissionDetail";
import AdminJudges from "./pages/admin/AdminJudges";
import JudgeDashboard from "./pages/judge/JudgeDashboard";
import JudgeScore from "./pages/judge/JudgeScore";
import AdminCompetition from "./pages/admin/AdminCompetition";
import VideoPitch from "./pages/VideoPitch";
import Fundraising from "./pages/Fundraising";
import Notifications from "./pages/Notifications";
import Implementation from "./pages/Implementation";
import Profile from "./pages/Profile";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminFundraising from "./pages/admin/AdminFundraising";
import AdminImplementation from "./pages/admin/AdminImplementation";
import AdminTeams from "./pages/admin/AdminTeams";
import AdminTeamDetail from "./pages/admin/AdminTeamDetail";

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Public */}
      <Route path={_route._landing} element={<Landing />} />
      <Route path={_route._errorpage} element={<ErrorPage />} />

      {/* Auth */}
      <Route element={<PageLayouts.AuthLayout />}>
        <Route path={_route._login} element={<Login />} />
        <Route path={_route._signup} element={<Signup />} />
        <Route path={_route._verify} element={<Verify />} />
        <Route path={_route._forgot_password} element={<ForgotPassword />} />
        <Route path={_route._reset_password} element={<ResetPassword />} />
      </Route>

      {/* Shared (any authenticated role) */}
      <Route element={<PrivateRoute allowedRoles={['participant', 'judge', 'admin']} />}>
        <Route element={<PageLayouts.DashboardLayout />}>
          <Route path={_route._notifications} element={<Notifications />} />
          <Route path={_route._profile} element={<Profile />} />
        </Route>
      </Route>

      {/* Participant Routes */}
      <Route element={<PrivateRoute allowedRoles={['participant']} />}>
        <Route element={<PageLayouts.DashboardLayout />}>
          <Route path={_route._dashboard} element={<Dashboard />} />
          <Route path={_route._team} element={<Team />} />
          <Route path={_route._submission_new} element={<Submission />} />
          <Route path={_route._submission_video} element={<VideoPitch />} />
          <Route path={_route._video_pitch} element={<VideoPitch />} />
          <Route path={_route._fundraising} element={<Fundraising />} />
          <Route path={_route._implementation} element={<Implementation />} />
        </Route>
      </Route>

      {/* Judge Routes */}
      <Route element={<PrivateRoute allowedRoles={['judge']} />}>
        <Route element={<PageLayouts.DashboardLayout />}>
          <Route path={_route._judge_dashboard} element={<JudgeDashboard />} />
          <Route path={_route._judge_score} element={<JudgeScore />} />
          <Route path={_route._judge_calibration} element={<div className="space-y-6 max-w-2xl"><h2 className="text-xl font-bold">Calibration Notes</h2><p className="text-sm text-grey-667 mb-4">Use this space to align with the panel on scoring standards before reviewing submissions.</p><div className="bg-white rounded-xl border border-grey-200 p-5"><h3 className="font-bold text-sm mb-3">Scoring Calibration Guide</h3><div className="space-y-3 text-sm text-grey-667"><p><strong>Score 1 (Poor):</strong> Fails to address the criterion. Major gaps or unrealistic.</p><p><strong>Score 2 (Below Average):</strong> Partially addresses the criterion but with significant weaknesses.</p><p><strong>Score 3 (Meets Standard):</strong> Adequately addresses the criterion. This is the minimum threshold to advance.</p><p><strong>Score 4 (Above Average):</strong> Strong response that goes beyond expectations in most areas.</p><p><strong>Score 5 (Excellent):</strong> Exceptional response. Clear, compelling, and thoroughly addresses the criterion.</p></div></div><div className="bg-white rounded-xl border border-grey-200 p-5"><h3 className="font-bold text-sm mb-3">4 Scoring Criteria</h3><div className="space-y-3 text-sm text-grey-667"><p><strong>Humanitarian Impact Potential:</strong> Does the project address a real and significant need? How many people could it benefit, and how meaningfully?</p><p><strong>Feasibility:</strong> Can this actually be implemented? Is the timeline realistic? Does the team have the capacity?</p><p><strong>Innovation & Originality:</strong> Is the approach creative, fresh, or an improvement on existing solutions?</p><p><strong>Clarity of Plan & Budget:</strong> Is the proposal well-organized, clearly written, and backed by a realistic budget?</p></div></div></div>} />
        </Route>
      </Route>

      {/* Admin Routes */}
      <Route element={<PrivateRoute allowedRoles={['admin']} />}>
        <Route element={<PageLayouts.DashboardLayout />}>
          <Route path={_route._admin_dashboard} element={<AdminDashboard />} />
          <Route path={_route._admin_competition} element={<AdminCompetition />} />
          <Route path={_route._admin_submissions} element={<AdminSubmissions />} />
          <Route path={_route._admin_submission_detail} element={<AdminSubmissionDetail />} />
          <Route path={_route._admin_teams} element={<AdminTeams />} />
          <Route path={_route._admin_team_detail} element={<AdminTeamDetail />} />
          <Route path={_route._admin_judges} element={<AdminJudges />} />
          <Route path={_route._admin_users} element={<AdminUsers />} />
          <Route path={_route._admin_notifications} element={<AdminNotifications />} />
          <Route path={_route._admin_fundraising} element={<AdminFundraising />} />
          <Route path={_route._admin_implementation} element={<AdminImplementation />} />
          <Route path={_route._admin_legal} element={<div className="p-6"><h2 className="text-xl font-bold mb-2">Legal Documents</h2><p className="text-grey-667">Legal document management coming soon.</p></div>} />
        </Route>
      </Route>
    </>
  )
);

function App() {
  return (
    <div id="App">
      <Toaster position="top-right" />
      <Sentry.ErrorBoundary
        fallback={({ resetError }) => (
          <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
            <h1 style={{ color: '#004177' }}>Something went wrong</h1>
            <p>Our team has been notified. Please refresh to try again.</p>
            <button
              onClick={resetError}
              style={{ marginTop: '16px', padding: '8px 16px', background: '#004177', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              Reload
            </button>
          </div>
        )}
      >
        <RouterProvider router={router} />
      </Sentry.ErrorBoundary>
    </div>
  );
}

export default App;
